/**
 * AICA Settings Screen - Unified Settings Page
 * 2-column sidebar layout; sections synced to ?section=.
 *
 * My Account (all users): Profile, Notifications.
 * Organization (manage): Organization, Team & Access (tabs: members /
 * invitations), Plan & Billing (tabs: usage / budget / balance / plan).
 * Integrations: Data Source (manage; tabs: connection / import),
 * API Keys (owner/client — same RBAC level as billing).
 * Compliance (manage or platform admin): Overview, Consent, DSR.
 *
 * Analysis setup (evaluation criteria, scripts, routing, CRM field mapping)
 * moved to the /analysis-setup hub — legacy ?section= values for those
 * permanently redirect there (see Route.beforeLoad below).
 *
 * Legacy section ids stay reachable through SECTION_ALIASES.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/contexts/RoleContext';
import { canManageDataRetention } from '@aica/core';
import type { GlobalRole } from '@aica/core';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useTier } from '@/contexts/TierContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AlertCircle, Construction } from 'lucide-react';
import { PageBody } from '@/components/layout/PageBody';
import { SkeletonForm } from '@/components/ui/skeleton';

import { useSettings } from './hooks/useSettings';
import { SettingsLayout, SettingsCard } from './components/SettingsLayout';
import { SettingsPageHeader } from './components/SettingsPageHeader';
import { UnsavedChangesGuard } from '@/components/settings/UnsavedChangesGuard';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ProfileTab } from './components/ProfileTab';
import { OrganizationTab } from './components/OrganizationTab';
import {
  DataSourcesSection,
  type DataSourceTabId,
} from './components/DataSourcesSection';
import { ApiKeysSection } from './components/ApiKeysSection';
import { ServiceSection } from './components/ServiceSection';
import { TeamSection } from './components/TeamSection';
import { InvitationsSection } from './components/InvitationsSection';
import { OrgStructureSection } from './components/OrgStructureSection';
import { PlanBillingSection, type BillingTabId } from './components/PlanBillingSection';
import { AccessLogSection } from './components/AccessLogSection';
import { ConsentSection } from './components/ConsentSection';
import { DSRSection } from './components/DSRSection';
import { ComplianceSection } from './components/ComplianceSection';
import { DemoSettingsPanel } from '@/features/settings/DemoSettingsPanel';

import { NotificationsHub } from '@/features/notifications/NotificationsHub';
import { canAccessSection } from './components/SettingsSidebar';
import type { SettingsSection } from './components/SettingsSidebar';
import type { SettingsData } from './types';

/**
 * Coming Soon placeholder for sections not yet implemented
 */
function ComingSoonSection({ title }: { title: string }) {
  const { t } = useTranslation(['settings', 'common']);

  return (
    <SettingsCard testId="settings-coming-soon">
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Construction className="h-12 w-12 text-fg-muted mb-4" aria-hidden="true" />
        <h3 className="text-base font-medium text-fg mb-2">{title}</h3>
        <p className="text-sm text-fg-muted max-w-md">
          {t('comingSoon', 'This section is coming soon. Stay tuned for updates!')}
        </p>
      </div>
    </SettingsCard>
  );
}

interface SettingsSectionContentProps {
  section: SettingsSection;
  /** Sub-tab to open initially (validated against SECTION_TABS in validateSearch). */
  initialTab?: string;
  canManageUsers: boolean;
  canSeeBilling: boolean;
  /**
   * Org structure (CRM import, department head assignment) — mirrors
   * `organization.manage_settings`: role `client` (org owner) + platform
   * owner only, `admin` does not qualify. Named separately from
   * `canSeeBilling` even though the formula is identical today — the two
   * gate unrelated permissions that only happen to share a role set right
   * now (see `canManageOrgStructure` in `packages/core/src/rbac/permissions.ts`).
   */
  canManageOrgStructure: boolean;
  isOwner: boolean;
  /**
   * The caller's org role. Needed alongside `isOwner` because the two answer
   * different questions: `isOwner` is the PLATFORM super-admin flag, while data
   * retention is authorized for the ORG owner (`client`) — see
   * `canManageDataRetention`.
   */
  globalRole: GlobalRole | null;
  /**
   * Организационный payload `GET /api/v1/settings` — или его отсутствие.
   *
   * `null` — рабочее состояние, а не ошибка загрузки: эндпоинт требует
   * `user.read`, который RBAC даёт только client и admin, поэтому у supervisor
   * и agent он 403-ит. Секции, которым payload не нужен (см.
   * SECTIONS_WITHOUT_ORG_SETTINGS), обязаны рендериться и без него — иначе роль
   * не доходит до собственных настроек уведомлений.
   */
  settings: SettingsData | null;
  saving: boolean;
  refs: {
    profileNameRef: React.RefObject<HTMLInputElement | null>;
    profileLastNameRef: React.RefObject<HTMLInputElement | null>;
    orgNameRef: React.RefObject<HTMLInputElement | null>;
    orgIndustryRef: React.RefObject<HTMLInputElement | null>;
    orgDescriptionRef: React.RefObject<HTMLTextAreaElement | null>;
    orgLanguageRef: React.RefObject<HTMLInputElement | null>;
    orgTimezoneRef: React.RefObject<HTMLInputElement | null>;
    orgWeekStartsOnRef: React.RefObject<HTMLInputElement | null>;
    [key: string]: unknown;
  };
  handleSaveProfile: () => Promise<void>;
  handleSaveOrganization: () => Promise<void>;
  /**
   * An edit was made in one of the ref-driven forms (profile, organization).
   * Those inputs are uncontrolled — nothing up here re-renders when they
   * change — so the screen cannot derive "dirty" from state and has to be
   * told. Only the two tabs that persist through `handleSave*` report it.
   */
  onEdit?: () => void;
}

/**
 * «Данных нет» ДЛЯ ОДНОЙ СЕКЦИИ, а не для всей страницы.
 *
 * `data-testid` сохранён прежним (`settings-empty`): это то же сообщение, что и
 * раньше, просто теперь оно занимает область контента, а навигация по секциям
 * остаётся на месте.
 */
function OrgSettingsUnavailable() {
  const { t } = useTranslation(['settings', 'common']);
  return (
    <div
      className="flex flex-1 items-center justify-center p-4"
      data-testid="settings-empty"
    >
      <div className="text-center text-fg-muted">{t('noSettingsData')}</div>
    </div>
  );
}

/**
 * Named component for settings section content - avoids inline render function
 *
 * Exported (not just used internally) so the content gate — the deep-link
 * path a URL can hit directly, bypassing the sidebar entirely — can be
 * render-tested on its own. See SettingsSectionContent.test.tsx.
 */
export function SettingsSectionContent({
  section,
  initialTab,
  canManageUsers,
  canSeeBilling,
  canManageOrgStructure,
  isOwner,
  globalRole,
  settings,
  saving,
  refs,
  handleSaveProfile,
  handleSaveOrganization,
  onEdit,
}: SettingsSectionContentProps) {
  const { t } = useTranslation(['settings', 'common']);
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const { tier } = useTier();

  // Single access gate driven by SECTION_PERMISSIONS and SECTION_FEATURE_FLAGS
  // — the same maps the sidebar uses for item visibility, so nav and content
  // stay in lockstep. A section switched off by flag is unreachable by deep
  // link too, which is the point: hiding only the nav item leaves the URL live.
  if (
    !canAccessSection(section, {
      canManageUsers,
      canSeeBilling,
      isOwner,
      isFeatureEnabled,
      isFreeTier: tier === 'free',
    })
  ) {
    return (
      <div data-testid="settings-page">
        <AccessDenied />
      </div>
    );
  }

  switch (section) {
    case 'profile':
      // Единственные две секции, которые читают организационный payload, сами и
      // отвечают за его отсутствие. Прежний блансетный `if (!settings)` уровнем
      // выше отдавал `settings-empty` на ВЕСЬ экран сразу после того, как гейт
      // ошибки специально отступил ради секций из
      // SECTIONS_WITHOUT_ORG_SETTINGS — из-за чего supervisor и agent так и не
      // доходили до своих уведомлений.
      if (!settings) return <OrgSettingsUnavailable />;
      return (
        // ProfileTab persists two plain text inputs, so a bubbled input event
        // is a complete dirty signal for this section.
        <div data-testid="settings-page" onInput={onEdit}>
          <SettingsCard testId="section-profile">
            <ProfileTab
              settings={settings}
              profileNameRef={refs.profileNameRef}
              profileLastNameRef={refs.profileLastNameRef}
              saving={saving}
              onSave={handleSaveProfile}
            />
          </SettingsCard>
        </div>
      );

    case 'notifications':
      // One screen for the whole chain (event -> recipient -> channel). The
      // role-defaults matrix moved here from the Organization section: it
      // decides who receives what, which has nothing to do with the
      // organization's name and timezone. Gated on `canManageUsers` because
      // this section is open to everyone, unlike its previous home.
      return <NotificationsHub canManageOrgDefaults={canManageUsers} />;

    case 'organization':
      if (!settings) return <OrgSettingsUnavailable />;
      return (
        <div data-testid="settings-page" className="space-y-4">
          <SettingsCard
            description={t(
              'organizationDescription',
              'Manage organization details and defaults'
            )}
            testId="section-organization"
          >
            <OrganizationTab
              settings={settings}
              saving={saving}
              canEditRetention={canManageDataRetention(globalRole, isOwner)}
              canEditAssistant={canManageOrgStructure}
              refs={{
                orgNameRef: refs.orgNameRef,
                orgIndustryRef: refs.orgIndustryRef,
                orgDescriptionRef: refs.orgDescriptionRef,
                orgLanguageRef: refs.orgLanguageRef,
                orgTimezoneRef: refs.orgTimezoneRef,
                orgWeekStartsOnRef: refs.orgWeekStartsOnRef,
              }}
              onSave={handleSaveOrganization}
              onEdit={onEdit}
            />
          </SettingsCard>
          <DemoSettingsPanel />
        </div>
      );

    case 'team': {
      // Team & Access: members (incl. pending access requests, rendered by
      // TeamSection itself), sent invitations, and — org owner only — org
      // structure (import + head assignment) as tabs of one section.
      //
      // The org-structure endpoints all require `organization.manage_settings`,
      // which RBAC restricts to role `client` (org owner) plus the platform
      // owner (packages/core/src/rbac/permissions.ts) — `admin` gets a 403 on
      // every one. Gated on its own `canManageOrgStructure` prop rather than
      // reusing `canSeeBilling` (whose formula is identical today only by
      // coincidence — see `canManageOrgStructure` in permissions.ts) so the
      // two can't silently drift apart if either permission's role set ever
      // changes independently.
      const defaultTab =
        initialTab === 'structure' && !canManageOrgStructure
          ? 'members'
          : (initialTab ?? 'members');

      return (
        <div data-testid="settings-page">
          <Tabs defaultValue={defaultTab}>
            <TabsList variant="underline" className="mb-4 px-0">
              <TabsTrigger
                value="members"
                variant="underline"
                data-testid="team-tab-members"
              >
                {t('teamTabs.members', 'Members')}
              </TabsTrigger>
              <TabsTrigger
                value="invitations"
                variant="underline"
                data-testid="team-tab-invitations"
              >
                {t('teamTabs.invitations', 'Invitations')}
              </TabsTrigger>
              {canManageOrgStructure && (
                <TabsTrigger
                  value="structure"
                  variant="underline"
                  data-testid="team-tab-structure"
                >
                  {t('teamTabs.structure', 'Org structure')}
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="members">
              <SettingsCard testId="section-team">
                <TeamSection />
              </SettingsCard>
            </TabsContent>
            <TabsContent value="invitations">
              <SettingsCard
                description={t(
                  'invitations.manageDescription',
                  'View and manage sent invitations'
                )}
                testId="section-invitations"
              >
                <InvitationsSection />
              </SettingsCard>
            </TabsContent>
            {canManageOrgStructure && (
              <TabsContent value="structure">
                <SettingsCard
                  description={t(
                    'orgStructure.description',
                    'Department heads see the records of everyone under them, regardless of project assignment'
                  )}
                  testId="section-org-structure"
                >
                  <OrgStructureSection />
                </SettingsCard>
              </TabsContent>
            )}
          </Tabs>
        </div>
      );
    }

    case 'service':
      return (
        <div data-testid="settings-page">
          <SettingsCard
            title={t('sections.service', 'Service status')}
            description={t(
              'service.description',
              'How your conversations move through processing'
            )}
            testId="section-service"
          >
            <ServiceSection />
          </SettingsCard>
        </div>
      );

    case 'billing':
      return (
        <div data-testid="settings-page">
          <PlanBillingSection initialTab={initialTab as BillingTabId | undefined} />
        </div>
      );

    case 'data_source':
      return (
        <div data-testid="settings-page">
          <SettingsCard>
            <DataSourcesSection initialTab={initialTab as DataSourceTabId | undefined} />
          </SettingsCard>
        </div>
      );

    case 'api_keys':
      return (
        <div data-testid="settings-page">
          <SettingsCard>
            <ApiKeysSection />
          </SettingsCard>
        </div>
      );

    case 'activity_log':
      return (
        <div data-testid="settings-page">
          <SettingsCard
            title={t('sections.activityLog', 'Access log')}
            description={t(
              'activityLogDescription',
              'Who changed whose access in the organization, and when'
            )}
            testId="section-activity-log"
          >
            <AccessLogSection />
          </SettingsCard>
        </div>
      );

    case 'compliance_settings':
      return (
        <div data-testid="settings-page">
          <SettingsCard
            title={t('compliance.title', 'Compliance')}
            description={t(
              'compliance.description',
              'Configure consent and jurisdiction settings for your organization'
            )}
            testId="section-compliance-settings"
          >
            <ComplianceSection />
          </SettingsCard>
        </div>
      );

    case 'consent':
      return (
        <div data-testid="settings-page">
          <SettingsCard
            title={t('sections.consent', 'Consent Management')}
            description={t('consentDescription', 'Manage consent status and audit trail')}
            testId="section-consent"
          >
            <ConsentSection />
          </SettingsCard>
        </div>
      );

    case 'dsr':
      return (
        <div data-testid="settings-page">
          <SettingsCard
            title={t('sections.dsr', 'Data Subject Requests')}
            description={t('dsrDescription', 'GDPR data subject requests management')}
            testId="section-dsr"
          >
            <DSRSection />
          </SettingsCard>
        </div>
      );

    default:
      return (
        <div data-testid="settings-page">
          <ComingSoonSection title={t('unknownSection')} />
        </div>
      );
  }
}

/**
 * Sections that need nothing from the organization-settings payload.
 *
 * `GET /api/v1/settings` requires `user.read`, which RBAC grants to client and
 * admin only — so for a supervisor or an agent it 403s. The page's error state
 * used to swallow EVERYTHING, including sections marked 'everyone': that role
 * had no way to reach their own notification settings at all. The hub fetches
 * its own data, so an org-payload failure is not its failure.
 */
const SECTIONS_WITHOUT_ORG_SETTINGS: ReadonlySet<SettingsSection> =
  // «Работа сервиса» (#1341) reads GET /dashboard/service on its own, and the
  // access log (#3136) — GET /access/events.
  new Set<SettingsSection>(['notifications', 'service', 'activity_log']);

/**
 * Where /settings opens when the URL names no section.
 *
 * `GET /api/v1/settings` needs `user.read` — client and admin only — and the
 * default landing section, Profile, renders that payload. So for a supervisor
 * or an agent the sidebar's plain `/settings` link 403'd and the whole page
 * became the error state, which is exactly the dead end
 * `SECTIONS_WITHOUT_ORG_SETTINGS` was introduced to remove; it just never
 * applied, because the check was fed the raw URL param and fell back to
 * 'profile'. Roles that cannot load the payload land on the section that does
 * not need it.
 */
export function defaultSettingsSection(
  globalRole: GlobalRole | null,
  isOwner: boolean
): SettingsSection {
  const canReadOrgSettings =
    isOwner || (globalRole !== null && ['client', 'admin'].includes(globalRole));
  return canReadOrgSettings ? 'profile' : 'notifications';
}

/** Exported for tests: does an org-settings failure hide this section? */
export function shouldBlockOnSettingsError(
  section: SettingsSection | undefined,
  errorSettings: string | null | undefined
): boolean {
  if (!errorSettings) return false;
  // No `?? 'profile'` here any more: the caller resolves the section it is
  // actually rendering first, so an undefined URL param cannot silently mean
  // "the one section that always blocks".
  return section === undefined || !SECTIONS_WITHOUT_ORG_SETTINGS.has(section);
}

function SettingsPage() {
  const { canManageUsers, canSeeBilling, canManageOrgStructure, globalRole } =
    usePermissions();
  const { isOwner } = useOrganization();
  const { t } = useTranslation(['settings', 'common']);
  useDocumentTitle(t('common:nav.settings', 'Settings'));
  const navigate = useNavigate();
  const { section: urlSection, tab: urlTab } = Route.useSearch();

  const {
    settings,
    loadingSettings,
    errorSettings,
    saving,
    refs,
    handleSaveProfile,
    handleSaveOrganization,
  } = useSettings();

  // Profile and Organization drive uncontrolled inputs through refs, so there
  // is no form state to compare against the server — the tabs report an edit
  // and the flag clears when the SAVE says it landed.
  //
  // It used to clear on every replacement of `settings`, on the reasoning that
  // only a save replaces it. It is not: any refetch of the org settings query
  // does, and the retention section (its own form, its own button) triggers
  // exactly that. Renaming the organization and then saving retention left the
  // rename in the inputs with the guard disarmed — one sidebar click away from
  // losing it silently. `null` still clears: that is the organization switch,
  // where the form is replaced wholesale.
  const [isDirty, setIsDirty] = useState(false);
  const markDirty = useCallback(() => setIsDirty(true), []);
  useEffect(() => {
    if (settings === null) setIsDirty(false);
  }, [settings]);
  /** A failed save keeps the warning armed — the edits are still unsaved. */
  const saveProfile = useCallback(async () => {
    if (await handleSaveProfile()) setIsDirty(false);
  }, [handleSaveProfile]);
  const saveOrganization = useCallback(async () => {
    if (await handleSaveOrganization()) setIsDirty(false);
  }, [handleSaveOrganization]);

  // Loading state
  if (loadingSettings) {
    return (
      <PageBody>
        <SettingsPageHeader />
        <div data-testid="settings-loading" aria-live="polite">
          <span className="sr-only">{t('common:common.loading', 'Loading...')}</span>
          <div className="space-y-6">
            <SkeletonForm fields={8} />
          </div>
        </div>
      </PageBody>
    );
  }

  // Error state
  // The section actually rendered — not the raw URL param.
  const effectiveSection = urlSection ?? defaultSettingsSection(globalRole, isOwner);

  if (shouldBlockOnSettingsError(effectiveSection, errorSettings)) {
    return (
      <PageBody>
        <SettingsPageHeader />
        <div
          className="flex items-center justify-center py-16"
          data-testid="settings-error"
          aria-live="assertive"
        >
          <div className="text-center">
            <AlertCircle
              className="mx-auto h-12 w-12 text-danger mb-4"
              aria-hidden="true"
            />
            <p className="text-danger" data-testid="settings-error-message">
              {t('failedToLoadSettings')}
            </p>
            <p className="mt-2 text-sm text-fg-muted">{errorSettings}</p>
          </div>
        </div>
      </PageBody>
    );
  }

  // Отсутствие организационного payload больше не гасит страницу целиком.
  //
  // Этот early return стоял ровно после того места, где гейт ошибки специально
  // отступает для секций из SECTIONS_WITHOUT_ORG_SETTINGS, — и отдавал
  // `settings-empty` вместо `<NotificationsHub />`. То есть supervisor и agent
  // по-прежнему не имели пути к собственным настройкам уведомлений: тот самый
  // тупик, который релиз в докстринге объявил убранным. Решение приняла та
  // секция, которая payload читает (профиль и организация), см.
  // `OrgSettingsUnavailable`.

  return (
    <>
      {/* Renaming the organization or switching its language used to be lost
          by one click on any sidebar item. Switching SECTIONS is still not
          guarded — same pathname, and the guard deliberately stays quiet
          there — so sections must keep saving before they hand over. */}
      <UnsavedChangesGuard isDirty={isDirty} />
      <SettingsLayout
        defaultSection={effectiveSection}
        activeSection={urlSection ?? effectiveSection}
        onSectionChange={(section) => {
          void navigate({
            to: '/settings',
            search: { section },
            replace: true,
          });
        }}
      >
        {(activeSection) => (
          <SettingsSectionContent
            section={activeSection}
            initialTab={activeSection === urlSection ? urlTab : undefined}
            canManageUsers={canManageUsers}
            canSeeBilling={canSeeBilling}
            canManageOrgStructure={canManageOrgStructure}
            isOwner={isOwner}
            globalRole={globalRole}
            settings={settings}
            saving={saving}
            refs={refs}
            handleSaveProfile={saveProfile}
            handleSaveOrganization={saveOrganization}
            onEdit={markDirty}
          />
        )}
      </SettingsLayout>
    </>
  );
}

/**
 * Access Denied component for unauthorized sections
 */
function AccessDenied() {
  const { t } = useTranslation(['settings', 'common']);

  return (
    <SettingsCard testId="settings-access-denied">
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-warning mb-4" aria-hidden="true" />
        <h3 className="text-base font-medium text-fg mb-2">
          {t('accessDenied', 'Access Denied')}
        </h3>
        <p className="text-sm text-fg-muted max-w-md">
          {t(
            'accessDeniedDescription',
            "You don't have permission to view this section. Contact your administrator."
          )}
        </p>
      </div>
    </SettingsCard>
  );
}

interface SettingsSearch {
  section?: SettingsSection;
  tab?: string;
}

// Former standalone sections folded into unified sections (billing, team
// tabs, data-source import tab). Kept as aliases so old deep links
// (notifications, bookmarks) keep working — each alias resolves to its
// parent section AND the sub-tab that replaced it, so ?section=invitations
// still lands on the Invitations tab, not just the Team section.
// crm_integration and routing_log used to alias into data_source/routing —
// both now redirect straight into the /analysis-setup hub (see
// Route.beforeLoad below), so neither is resolved here anymore.
const SECTION_ALIASES: Record<string, { section: SettingsSection; tab?: string }> = {
  subscription: { section: 'billing', tab: 'plan' },
  budget: { section: 'billing', tab: 'budget' },
  invitations: { section: 'team', tab: 'invitations' },
  crm_import: { section: 'data_source', tab: 'import' },
};

// Valid sub-tabs per section — anything else in ?tab= is dropped so stale
// URLs can't select a nonexistent tab (which would render no active tab).
const SECTION_TABS: Partial<Record<SettingsSection, readonly string[]>> = {
  team: ['members', 'invitations'],
  data_source: ['connection', 'import'],
  billing: ['usage', 'budget', 'balance', 'plan'],
};

export const Route = createFileRoute('/settings/')({
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>): SettingsSearch => {
    const raw = typeof search.section === 'string' ? search.section : undefined;
    const alias = raw ? SECTION_ALIASES[raw] : undefined;
    const section = alias?.section ?? (raw as SettingsSection | undefined);
    const requestedTab =
      alias?.tab ?? (typeof search.tab === 'string' ? search.tab : undefined);
    const tab =
      section && requestedTab && SECTION_TABS[section]?.includes(requestedTab)
        ? requestedTab
        : undefined;
    return { section, tab };
  },
  // Sections folded into the /analysis-setup hub redirect there permanently.
  // `search` here is already post-validateSearch (aliases resolved, unknown
  // tabs stripped) — the problem for `crm_integration` and `routing_log`:
  // by the time `search` reaches this function, `crm_integration` has
  // already become `data_source` via SECTION_ALIASES (unmatchable by value
  // here), and `routing_log`/`?tab=delivery` have had their tab dropped by
  // SECTION_TABS (neither `routing` nor `delivery` is a valid entry anymore
  // — both folded into the hub). `location.search` is the router's raw,
  // pre-route-validateSearch parse of the URL query string (confirmed
  // against @tanstack/router-core: it's populated from history/parseLocation
  // in Router.parseLocation, independent of any given route's
  // validateSearch, which only feeds `match.search`) — reading straight off
  // it catches all three before validateSearch resolves/strips them.
  beforeLoad: ({ search, location }) => {
    const rawSearch = location.search as Record<string, unknown>;
    const rawSection = rawSearch.section;
    const rawTab = rawSearch.tab;

    if (rawSection === 'crm_integration') {
      throw redirect({ to: '/analysis-setup/crm-updates', replace: true });
    }

    if (rawSection === 'routing_log') {
      throw redirect({
        to: '/analysis-setup/routing',
        replace: true,
        search: { tab: 'log' },
      });
    }

    const target: Record<string, { to: string }> = {
      evaluation: { to: '/analysis-setup/categories' },
      scripts: { to: '/analysis-setup/scripts' },
      routing: { to: '/analysis-setup/routing' },
    };
    const hit = search.section ? target[search.section] : undefined;
    if (hit) {
      throw redirect({
        to: hit.to,
        replace: true,
        search: rawTab === 'log' ? { tab: 'log' } : undefined,
      });
    }

    if (rawSection === 'data_source' && rawTab === 'delivery') {
      throw redirect({ to: '/analysis-setup/crm-updates', replace: true });
    }
  },
});
